import React, { useCallback, useEffect, useState } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	Paper,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { obtenerBloqueosAgenda } from 'scripts/obtenerBloqueosAgenda';
import { crearBloqueoAgenda } from 'scripts/crearBloqueoAgenda';
import { actualizarBloqueoAgenda } from 'scripts/actualizarBloqueoAgenda';
import { eliminarBloqueoAgenda } from 'scripts/eliminarBloqueoAgenda';

const BLOQUEO_INICIAL = {
	id: null,
	fecha_desde: '',
	fecha_hasta: '',
	motivo: '',
};

const AgendaBloqueadaManagement = () => {
	const [bloqueos, setBloqueos] = useState([]);
	const [open, setOpen] = useState(false);
	const [bloqueoActual, setBloqueoActual] = useState(BLOQUEO_INICIAL);

	const token = sessionStorage.getItem('token');

	const fetchBloqueos = useCallback(async () => {
		try {
			const data = await obtenerBloqueosAgenda(token);
			setBloqueos(data);
		} catch (error) {
			toast.error(error.message || 'No se pudieron obtener los bloqueos de agenda.');
		}
	}, [token]);

	useEffect(() => {
		fetchBloqueos();
	}, [fetchBloqueos]);

	const handleOpen = (bloqueo = null) => {
		if (bloqueo) {
			setBloqueoActual({
				id: bloqueo.id,
				fecha_desde: bloqueo.fecha_desde || '',
				fecha_hasta: bloqueo.fecha_hasta || '',
				motivo: bloqueo.motivo || '',
			});
		} else {
			setBloqueoActual(BLOQUEO_INICIAL);
		}
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setBloqueoActual(BLOQUEO_INICIAL);
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setBloqueoActual((prev) => ({ ...prev, [name]: value }));
	};

	const validarBloqueo = () => {
		if (!bloqueoActual.fecha_desde || !bloqueoActual.fecha_hasta) {
			toast.error('Las fechas desde y hasta son obligatorias.');
			return false;
		}
		if (bloqueoActual.fecha_hasta < bloqueoActual.fecha_desde) {
			toast.error('La fecha hasta no puede ser menor que la fecha desde.');
			return false;
		}
		return true;
	};

	const handleGuardar = async () => {
		if (!validarBloqueo()) {
			return;
		}

		try {
			if (bloqueoActual.id) {
				await actualizarBloqueoAgenda(
					bloqueoActual.id,
					bloqueoActual.fecha_desde,
					bloqueoActual.fecha_hasta,
					bloqueoActual.motivo,
					token,
				);
				toast.success('Bloqueo actualizado correctamente.');
			} else {
				await crearBloqueoAgenda(
					bloqueoActual.fecha_desde,
					bloqueoActual.fecha_hasta,
					bloqueoActual.motivo,
					token,
				);
				toast.success('Bloqueo creado correctamente.');
			}
			handleClose();
			fetchBloqueos();
		} catch (error) {
			toast.error(error.message || 'No se pudo guardar el bloqueo.');
		}
	};

	const handleEliminar = async (id) => {
		const confirmar = window.confirm('¿Eliminar este bloqueo de agenda?');
		if (!confirmar) {
			return;
		}

		try {
			await eliminarBloqueoAgenda(id, token);
			toast.success('Bloqueo eliminado correctamente.');
			fetchBloqueos();
		} catch (error) {
			toast.error(error.message || 'No se pudo eliminar el bloqueo.');
		}
	};

	return (
		<div style={{ marginTop: '20px' }}>
			<Box sx={{ marginBottom: 2, paddingBottom: 2, borderBottom: '2px solid #ccc' }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={2} md={1}>
						<Button variant="contained" color="success" onClick={() => handleOpen()}>
							<AddIcon />
						</Button>
					</Grid>
				</Grid>
			</Box>

			<TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
				<Table>
					<TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#FFFFFF', zIndex: 1 }}>
						<TableRow>
							<TableCell>Fecha desde</TableCell>
							<TableCell>Fecha hasta</TableCell>
							<TableCell>Motivo</TableCell>
							<TableCell>Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{bloqueos.length > 0 ? (
							bloqueos.map((bloqueo) => (
								<TableRow key={bloqueo.id}>
									<TableCell>{bloqueo.fecha_desde}</TableCell>
									<TableCell>{bloqueo.fecha_hasta}</TableCell>
									<TableCell>{bloqueo.motivo || '-'}</TableCell>
									<TableCell>
										<Button
											variant="outlined"
											color="primary"
											onClick={() => handleOpen(bloqueo)}
											sx={{ marginRight: 2 }}
										>
											Editar
										</Button>
										<Button
											variant="outlined"
											color="secondary"
											onClick={() => handleEliminar(bloqueo.id)}
										>
											Eliminar
										</Button>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={4} style={{ textAlign: 'center' }}>
									No hay bloqueos de agenda cargados.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>{bloqueoActual.id ? 'Editar Bloqueo' : 'Agregar Bloqueo'}</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 2 }}>
						<TextField
							label="Fecha desde *"
							name="fecha_desde"
							type="date"
							value={bloqueoActual.fecha_desde}
							onChange={handleInputChange}
							fullWidth
							InputLabelProps={{ shrink: true }}
							sx={{ marginBottom: 2 }}
						/>
						<TextField
							label="Fecha hasta *"
							name="fecha_hasta"
							type="date"
							value={bloqueoActual.fecha_hasta}
							onChange={handleInputChange}
							fullWidth
							InputLabelProps={{ shrink: true }}
							sx={{ marginBottom: 2 }}
						/>
						<TextField
							label="Motivo (opcional)"
							name="motivo"
							value={bloqueoActual.motivo}
							onChange={handleInputChange}
							fullWidth
							multiline
							minRows={2}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="secondary">
						Cancelar
					</Button>
					<Button onClick={handleGuardar} color="primary">
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default AgendaBloqueadaManagement;
